import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut } from "lucide-react";
import { adminLogin, API } from "@/lib/api";
import SEO from "@/components/SEO";
import AdminInquiries from "@/components/admin/AdminInquiries";
import AdminProjects from "@/components/admin/AdminProjects";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import AdminInvestors from "@/components/admin/AdminInvestors";

const TOKEN_KEY = "verde_admin_token";
const LOGO = "/verde2050-logo-silver.png";
const TABS = [["inquiries", "Inquiries", AdminInquiries], ["investors", "Investor Circle", AdminInvestors], ["projects", "Projects", AdminProjects], ["content", "Content & links", AdminSettings], ["analytics", "Analytics", AdminAnalytics]];

function Login({ onToken }) {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const tk = await adminLogin(password);
            sessionStorage.setItem(TOKEN_KEY, tk);
            onToken(tk);
        } catch { toast.error("Wrong password"); } finally { setBusy(false); }
    };
    return (
        <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6">
            <Link to="/" className="absolute top-8 left-8 mono-label text-muted_text hover:text-forest flex items-center gap-2" data-testid="back-to-site"><ArrowLeft className="h-3.5 w-3.5" /> Back to site</Link>
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-2xl bg-surface p-10 space-y-6">
                <img src="/verde2050-logo-green.png" alt="Verde 2050" className="h-7 w-auto" data-testid="admin-login-logo" />
                <p className="mono-label text-moss">Admin</p>
                <h1 className="font-serif text-4xl text-forest">Sign in.</h1>
                <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="editorial-input" placeholder="Admin password" autoFocus />
                <button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full rounded-full bg-forest text-cream py-4 text-sm font-medium disabled:opacity-60 inline-flex justify-center">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </button>
            </form>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [tab, setTab] = useState("inquiries");
    const client = useMemo(() => {
        const c = axios.create({ baseURL: API, headers: { "X-Admin-Password": token } });
        c.interceptors.response.use((r) => r, (err) => {
            if (err?.response?.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setToken(""); toast.error("Session expired. Sign in again."); }
            return Promise.reject(err);
        });
        return c;
    }, [token]);

    if (!token) return <><SEO path="/admin" title="Admin" description="Admin" noindex /><Login onToken={setToken} /></>;
    const Active = TABS.find((t) => t[0] === tab)[2];

    return (
        <div className="min-h-screen bg-cream text-ink" data-testid="admin-dashboard">
            <SEO path="/admin" title="Admin" description="Admin" noindex />
            <header className="bg-forest sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/"><img src={LOGO} alt="Verde 2050 — Admin" className="h-[18px] w-auto" /></Link>
                    <button type="button" onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-cream"><LogOut className="h-4 w-4" /> Sign out</button>
                </div>
            </header>
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                <nav className="flex flex-wrap gap-2 mb-10" aria-label="Admin sections">
                    {TABS.map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                                className={`rounded-full px-5 py-2.5 text-sm ${tab === k ? "bg-forest text-cream" : "bg-surface text-forest hover:bg-sage/40"}`}>{label}</button>
                    ))}
                </nav>
                <Active client={client} token={token} />
            </div>
        </div>
    );
}
